import { Sparkles, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import type { ChatContextHit } from '../api/chat-stream.js';
import { useChat, type ChatMessage } from '../hooks/use-chat.js';
import { cn } from '../lib/cn.js';
import { useUiStore } from '../state/ui-store.js';

import { ChatInputBar } from './chat-input-bar.js';
import { ChatMessageBubble } from './chat-message.js';
import { ToolCallCard } from './tool-call-card.js';

export function RightPanel(): JSX.Element {
  const open = useUiStore((s) => s.rightPanelOpen);
  const setOpen = useUiStore((s) => s.setRightPanelOpen);
  const setSelectedNodeId = useUiStore((s) => s.setSelectedNodeId);
  const { name } = useParams<{ name: string }>();
  const slug = name ?? '';

  const { messages, isStreaming, send, clear, stop } = useChat(slug);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef(true);

  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);

  useEffect(() => {
    if (!stickyRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    stickyRef.current = distance < 24;
  };

  const handleHitClick = (hit: ChatContextHit): void => {
    if (hit.id) setSelectedNodeId(hit.id);
  };

  const handleCitationClick = (label: string, nodeName: string): void => {
    setSelectedNodeId(`${label}:${nodeName}`);
  };

  const handleSend = (text: string): void => {
    send(text);
    stickyRef.current = true;
  };

  return (
    <aside
      aria-label="Chat panel"
      className={cn(
        'glass-panel scrollbar-thin absolute inset-y-0 right-0 z-[5] flex w-[420px] flex-col border-l border-border-subtle text-text-primary shadow-panel transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <header className="relative flex items-center justify-between border-b border-dashed border-border-subtle px-4 py-3">
        <div
          className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent"
          aria-hidden
        />
        <div className="flex items-center gap-2">
          <span
            className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-accent via-fuchsia-500 to-rose-500 text-white shadow-glow"
            aria-hidden
          >
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">CodeGraph AI</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
              {isStreaming ? 'streaming…' : 'ready'}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close chat panel"
          className="grid h-7 w-7 place-items-center rounded-md text-text-muted transition-colors hover:bg-hover hover:text-text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="scrollbar-thin flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4"
      >
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          messages.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              onHitClick={handleHitClick}
              onCitationClick={handleCitationClick}
            />
          ))
        )}
      </div>

      <ChatInputBar
        disabled={!slug}
        isStreaming={isStreaming}
        onSend={handleSend}
        onClear={clear}
        onStop={stop}
      />
    </aside>
  );
}

function EmptyState(): JSX.Element {
  const tips = [
    'Where is `UserService.create` called?',
    'Show me upstream callers of `processOrder`',
    'List all auth middleware files',
  ];
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-accent via-fuchsia-500 to-rose-500 text-white shadow-glow animate-pulse-glow">
        <Sparkles className="h-6 w-6" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-text-primary">Ask anything about this repo</p>
        <p className="text-xs text-text-muted">
          Streaming answers, tool calls, and citations appear here.
        </p>
      </div>
      <div className="flex w-full flex-col gap-1.5 pt-2">
        {tips.map((t) => (
          <div
            key={t}
            className="rounded-lg border border-dashed border-border-subtle bg-surface/40 px-3 py-2 text-left font-mono text-[11px] text-text-secondary"
          >
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function MessageRow({
  message,
  onHitClick,
  onCitationClick,
}: {
  readonly message: ChatMessage;
  readonly onHitClick: (hit: ChatContextHit) => void;
  readonly onCitationClick: (label: string, name: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <ChatMessageBubble message={message} onCitationClick={onCitationClick} />
      {message.contextHits && message.contextHits.length > 0 && (
        <ToolCallCard hits={message.contextHits} onHitClick={onHitClick} />
      )}
    </div>
  );
}
